export const diContainers = [];
let forcedScope = 'root';
/**
 * Force the scope of injectable dependencies and dependencies injections.
 **/
export function diForceScope(scope) {
    forcedScope = scope;
}
/**
 * Uses given scope for all di injections within callback
 */
export function runWithDiScope(scope, callback) {
    const currentScope = forcedScope;
    diForceScope(scope);
    callback();
    diForceScope(currentScope);
}
export function diInjectable(token, ctor, scope = forcedScope) {
    var _a;
    const container = (_a = getContainer(scope)) !== null && _a !== void 0 ? _a : new DIContainer(scope);
    if (!container.tokenMap[token]) {
        container.tokenMap[token] = {
            ctor,
            args: [],
            instantiating: false,
            instances: [],
            dependencies: []
        };
    }
    return {
        withArgs(...resolveArgs) {
            container.tokenMap[token].args = resolveArgs;
        }
    };
}
/** Register an already instantiated dependency instance */
export function registerDependency(token, value, scope = forcedScope) {
    var _a;
    const container = (_a = getContainer(scope)) !== null && _a !== void 0 ? _a : new DIContainer(scope);
    if (!container.tokenMap[token]) {
        container.tokenMap[token] = {
            ctor: undefined,
            args: [],
            instantiating: false,
            dependencies: [],
            instances: [value]
        };
    }
}
/** Get injectable. Parses through containers from newest to oldest created */
function getInjectableClass(token) {
    for (const container of diContainers.slice().reverse()) {
        const tokenMap = container.tokenMap[token];
        if (tokenMap) {
            return tokenMap.ctor;
        }
    }
    return undefined;
}
function getInjectableInstance(token, scope) {
    for (const container of diContainers.slice().reverse()) {
        const tokenMap = container.tokenMap[token];
        const matchingScope = scope ? scope === container.scope : true;
        if (tokenMap && matchingScope) {
            return tokenMap.instances[0];
        }
    }
    return undefined;
}
function getContainer(scope) {
    return diContainers.find(container => container.scope === scope);
}
export function destroyDiContainer(scope) {
    const idx = diContainers.findIndex(container => container.scope === scope);
    if (diContainers[idx]) {
        diContainers[idx].tokenMap = {};
        diContainers.splice(idx, 1);
    }
}
export function destroyDiInstance(instance) {
    for (const container of diContainers) {
        const metadatas = Object.values(container.tokenMap);
        for (const metadata of metadatas) {
            metadata.instances = metadata.instances.filter(item => item !== instance);
        }
    }
}
export function clearDiContainers() {
    const studioContainer = getContainer('studio');
    for (const container of diContainers) {
        if (container !== studioContainer) {
            container.tokenMap = {};
        }
    }
    diContainers.splice(0);
    if (studioContainer) {
        diContainers.push(studioContainer);
    }
}
export class DIContainer {
    constructor(scope = 'root') {
        this.tokenMap = {};
        this.scope = scope;
        diContainers.push(this);
    }
    instantiate(token, metadata) {
        return this.factory(token, metadata);
    }
    factory(token, { ctor, args }) {
        const dependency = this.tokenMap[token];
        if (!dependency || dependency.instantiating) {
            return;
        }
        dependency.instantiating = true;
        this.currentlyCreating = dependency;
        if (!ctor) {
            return;
        }
        const instance = new ctor(...(args.length ? args : []));
        dependency.instantiating = false;
        dependency.instances.push(instance);
        if (this.currentlyCreating === dependency) {
            this.currentlyCreating = undefined;
        }
        return instance;
    }
}
export function diInject(token, { args, optional, scope = forcedScope, asNew } = {}) {
    var _a;
    if (!asNew) {
        const dependencyInstance = getInjectableInstance(token, scope);
        if (dependencyInstance) {
            return dependencyInstance;
        }
    }
    const ctor = getInjectableClass(token);
    if (!ctor) {
        if (optional) {
            return;
        }
        throw new Error(`Could not get injectable of token ${token}. Expected dependency constructor has probably not been initialized as an Injectable.`);
    }
    diInjectable(token, ctor, scope);
    const container = getContainer(scope);
    if (!container) {
        if (optional) {
            return;
        }
        throw new Error(`Non-optional injection on container with scope ${scope}`);
    }
    const rootContainer = getContainer('root');
    if (!rootContainer && !container) {
        throw new Error(`No container found during injection`);
    }
    const dependency = (_a = container.tokenMap[token]) !== null && _a !== void 0 ? _a : rootContainer === null || rootContainer === void 0 ? void 0 : rootContainer.tokenMap[token];
    if (!dependency) {
        if (optional) {
            return;
        }
        throw new Error(`Token ${token} is not registered for dependency injection.`);
    }
    if (args) {
        dependency.args = args;
    }
    if (container.currentlyCreating) {
        container.currentlyCreating.dependencies.push(dependency);
        for (const dep of container.currentlyCreating.dependencies) {
            const ciruclarDeps = checkCircularDependencies(dep, dependency);
            if (ciruclarDeps.length && dep.ctor) {
                throw new Error(`Circular dependency detected during instantiation. ${dep.ctor.name} => ${ciruclarDeps
                    .map(({ ctor }) => ctor === null || ctor === void 0 ? void 0 : ctor.name)
                    .join(' => ')}`);
            }
        }
    }
    const instance = container.instantiate(token, dependency);
    container.currentlyCreating = undefined;
    return instance;
}
function checkCircularDependencies(dependencyA, dependencyB) {
    const deps = [];
    function checkRecursively(depA, depB) {
        for (const dependency of depA.dependencies) {
            if (dependency.dependencies.some(({ ctor }) => ctor === depB.ctor)) {
                deps.push(dependency, depB);
                return;
            }
            checkRecursively(dependency, depB);
        }
    }
    checkRecursively(dependencyA, dependencyB);
    return deps;
}
